import type { Moment } from '@domain/events.ts'
import type { FamilyState, Member, Task } from '@domain/state.ts'
import { memberDayOf, routinesOf, taskDayOf, tasksOf } from '@domain/state.ts'
import { es } from '../strings/es.ts'
import { Meter } from './Meter.tsx'
import { Pill } from './Pill.tsx'

const MOMENT_LABEL: Record<Moment, string> = {
  morning: es.today.morning,
  afternoon: es.today.afternoon,
  evening: es.today.evening,
}

/** Which routine is due now. The day has three, and only one is "now". */
export function momentFor(hour: number): Moment {
  if (hour < 13) return 'morning'
  if (hour < 20) return 'afternoon'
  return 'evening'
}

export function levelFor(xp: number): number {
  return Math.floor(xp / 100) + 1
}

function TaskRow({
  task,
  state,
  day,
  onOpen,
}: {
  task: Task
  state: FamilyState
  day: string
  onOpen: (task: Task) => void
}) {
  const today = taskDayOf(state, day as never, task.memberId, task.id)
  const done = task.steps.filter((s) => today.steps.has(s.id)).length
  const accent = state.members.get(task.memberId)?.accent ?? '#1c6a73'

  return (
    <button
      type="button"
      onClick={() => {
        onOpen(task)
      }}
      className="border-rule bg-surface flex w-full items-center gap-4 rounded-xl border p-4 text-left"
    >
      <span aria-hidden="true" className="text-4xl">
        {task.emoji ?? '⭐'}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-lg font-bold">{task.name}</span>
        {today.status === 'marked' ? (
          <Pill tone="wait">{es.today.waiting}</Pill>
        ) : today.status === 'validated' ? (
          <Pill tone="done">{es.today.validated}</Pill>
        ) : today.returnedNote !== undefined ? (
          <Pill tone="back">{es.today.returned}</Pill>
        ) : task.steps.length > 0 ? (
          <span className="mt-1 block">
            <span className="text-muted block text-sm">
              {es.today.stepsDone(done, task.steps.length)}
            </span>
            {done > 0 ? (
              <span className="mt-1 block">
                <Meter done={done} total={task.steps.length} accent={accent} />
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
      {task.kind === 'mission' && today.status === 'pending' ? (
        <span className="text-gold shrink-0 text-lg font-extrabold whitespace-nowrap">
          +{task.rewards[task.rung].coins}
        </span>
      ) : null}
    </button>
  )
}

export function Today({
  member,
  state,
  day,
  moment,
  chestBlocked,
  onOpenTask,
  onOpenChest,
  onShop,
  onTimer,
  onJuegos,
  onBack,
}: {
  member: Member
  state: FamilyState
  day: string
  moment: Moment
  chestBlocked: string | null
  onOpenTask: (task: Task) => void
  onOpenChest: () => void
  onShop: () => void
  onTimer: () => void
  onJuegos: () => void
  onBack: () => void
}) {
  const order: Moment[] = ['morning', 'afternoon', 'evening']
  const rotated = [...order.slice(order.indexOf(moment)), ...order.slice(0, order.indexOf(moment))]
  const routineGroups = rotated
    .map((slot) => ({ slot, tasks: routinesOf(state, member.id, slot) }))
    .filter((group) => group.tasks.length > 0)
  const missions = tasksOf(state, member.id, 'mission').filter(
    (task) => taskDayOf(state, day as never, member.id, task.id).status !== 'validated',
  )
  const today = memberDayOf(state, day as never, member.id)

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-28">
      <header className="mb-6 flex items-center gap-3">
        <button type="button" onClick={onBack} className="text-muted text-sm font-bold">
          {es.today.back}
        </button>
        <span className="flex-1" />
        <span className="text-gold text-lg font-extrabold">
          {member.coins}{' '}
          <span className="text-muted text-sm font-bold">{member.coinWord ?? es.today.coins}</span>
        </span>
      </header>

      <h1 className="mb-1 text-3xl font-extrabold tracking-tight" style={{ color: member.accent }}>
        {member.emoji} {member.name}
      </h1>
      <p className="text-muted mt-0 mb-6 text-sm font-bold">
        {es.today.level(levelFor(member.xp))} · {member.xp} {es.today.xp}
      </p>

      {routineGroups.map((group, index) => (
        <section key={group.slot} className="mb-8">
          <h2 className="text-muted mb-3 text-xs font-bold tracking-[0.1em] uppercase">
            {index === 0 ? `${es.today.nowLabel} · ` : ''}
            {MOMENT_LABEL[group.slot]}
          </h2>
          <div className="grid gap-3">
            {group.tasks.map((task) => (
              <TaskRow key={task.id} task={task} state={state} day={day} onOpen={onOpenTask} />
            ))}
          </div>
        </section>
      ))}

      <section className="mb-8">
        <h2 className="text-muted mb-3 text-xs font-bold tracking-[0.1em] uppercase">
          {es.today.missions}
        </h2>
        {missions.length === 0 ? (
          <p className="text-muted m-0">{es.today.noMissions}</p>
        ) : (
          <div className="grid gap-3">
            {missions.map((task) => (
              <TaskRow key={task.id} task={task} state={state} day={day} onOpen={onOpenTask} />
            ))}
          </div>
        )}
      </section>

      <section className="border-gold bg-gold-soft rounded-xl border-t-4 p-5">
        <h2 className="m-0 mb-2 text-lg font-extrabold">
          🎁 {es.today.chestOpened(today.chestMinutes)}
        </h2>
        {today.chestOpened ? (
          <button
            type="button"
            className="bg-teal mt-2 rounded-lg px-6 text-base font-bold text-white"
            onClick={onTimer}
          >
            {es.today.timer}
          </button>
        ) : chestBlocked === null ? (
          <>
            <p className="m-0 mb-3 text-sm font-bold">{es.today.chestReady}</p>
            <button
              type="button"
              className="bg-teal rounded-lg px-6 text-base font-bold text-white"
              onClick={onOpenChest}
            >
              {es.today.chestOpen}
            </button>
          </>
        ) : (
          <p className="m-0 text-sm">{chestBlocked}</p>
        )}
      </section>

      <nav className="border-rule bg-surface fixed inset-x-0 bottom-0 flex justify-around border-t p-2">
        <button type="button" className="flex-1 text-base font-bold" onClick={onShop}>
          🛒 {es.today.shop}
        </button>
        <button type="button" className="flex-1 text-base font-bold" onClick={onTimer}>
          ⏱️ {es.today.timer}
        </button>
        <button type="button" className="flex-1 text-base font-bold" onClick={onJuegos}>
          🎮 {es.today.juegos}
        </button>
      </nav>
    </div>
  )
}
